import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, Upload, Send,
  CheckCircle, XCircle, RefreshCw, Clock, ArrowRight, AlertTriangle
} from "lucide-react";
import { useExchangeRate, formatUSD, formatHTG, htgToUsd } from "./lib/currency";
import { uploadPaymentProof } from "./lib/uploadProof";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

// Admin contact info (fallback when QR codes not loaded)
const ADMIN_PAYMENT_NUMBERS = ["+50947096761", "+50946261180", "+50955460588"];
const ADMIN_WHATSAPP = "50946261180";
// Official WhatsApp group — used for "Recharger" and "Payer avec mon solde" flows
const ADMIN_WHATSAPP_URL = "https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?mode=gi_t";

// ================================================================
// User balance widget (client OR affiliate)
// ================================================================
export const BalanceWidget = ({ apiCall, API, tokenKey = "token", role = "client" }) => {
  const [balance, setBalance] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qrCodes, setQrCodes] = useState([]);
  // Pending deposits — show the user that their recharge is awaiting admin validation
  const [pendingDeposits, setPendingDeposits] = useState([]);

  // Forms
  const [showDeposit, setShowDeposit] = useState(false);
  const [depAmount, setDepAmount] = useState("");
  const [depProof, setDepProof] = useState("");
  const [depUploading, setDepUploading] = useState(false);

  const [showWithdraw, setShowWithdraw] = useState(false);
  const [wdrAmount, setWdrAmount] = useState("");
  const [wdrDest, setWdrDest] = useState("");

  const [showTransfer, setShowTransfer] = useState(false);
  const [trfPhone, setTrfPhone] = useState("");
  const [trfAmount, setTrfAmount] = useState("");
  const [trfNote, setTrfNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/balance");
      const next = res.data.balance || 0;
      setBalance(prev => {
        if (next > prev + 0.001 && prev > 0) {
          toast.success(`💰 +${(next - prev).toFixed(2)} G — Nouveau solde : ${next.toFixed(2)} G`, { duration: 6000 });
        }
        return next;
      });
      setLedger(res.data.ledger || []);
    } catch (e) {
      // silent
    } finally { setLoading(false); }
    try {
      const r = await apiCall("get", "/payment-qr-codes");
      setQrCodes(r.data.qr_codes || []);
    } catch { /* ignore */ }
    try {
      const dr = await apiCall("get", "/balance/my-deposits?limit=20");
      const all = dr.data?.deposits || [];
      setPendingDeposits(all.filter(d => d.status === "pending"));
    } catch { /* ignore */ }
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  // === Auto-refresh balance every 15s while the tab is visible ===
  useEffect(() => {
    let intervalId = null;
    const refresh = async () => {
      try {
        const res = await apiCall("get", "/balance");
        const next = res.data.balance || 0;
        setBalance(prev => {
          if (next > prev + 0.001 && prev > 0) {
            toast.success(`💰 +${(next - prev).toFixed(2)} G — Nouveau solde : ${next.toFixed(2)} G`, { duration: 6000 });
          }
          return next;
        });
        setLedger(res.data.ledger || []);
      } catch { /* silent */ }
      try {
        const dr = await apiCall("get", "/balance/my-deposits?limit=20");
        const all = dr.data?.deposits || [];
        setPendingDeposits(all.filter(d => d.status === "pending"));
      } catch { /* silent */ }
    };
    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        if (document.visibilityState === "visible") refresh();
      }, 15000);
    };
    const stop = () => { if (intervalId) { clearInterval(intervalId); intervalId = null; } };
    start();
    const onVisibility = () => { if (document.visibilityState === "visible") { refresh(); start(); } else { stop(); } };
    const onFocus = () => refresh();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); window.removeEventListener("focus", onFocus); };
  }, [apiCall]);

  const uploadProof = async (file) => {
    try {
      const data = await uploadPaymentProof(file, { role, tokenKey });
      return data.url;
    } catch (e) {
      toast.error(e.message || "Erreur lors du téléversement", { duration: 6000 });
      return null;
    }
  };

  const handleDepProof = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setDepUploading(true);
    const url = await uploadProof(f);
    if (url) { setDepProof(url); toast.success("Preuve téléversée"); }
    setDepUploading(false);
    // Reset input so the user can re-pick the same file after an error.
    try { e.target.value = ""; } catch (_) { /* noop */ }
  };

  const submitDeposit = async () => {
    if (!depAmount || Number(depAmount) <= 0) { toast.error("Montant invalide"); return; }
    if (!depProof) { toast.error("Preuve requise"); return; }
    try {
      await apiCall("post", "/balance/deposit", {
        amount: Number(depAmount),
        proof_url: depProof,
      });
      toast.success("✅ Dépôt soumis — en attente de validation par l'administrateur", { duration: 6000 });
      setDepAmount(""); setDepProof(""); setShowDeposit(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitWithdraw = async () => {
    if (!wdrAmount || Number(wdrAmount) <= 0) { toast.error("Montant invalide"); return; }
    if (!wdrDest.trim()) { toast.error("Numéro destination requis"); return; }
    try {
      await apiCall("post", "/balance/withdraw", {
        amount: Number(wdrAmount),
        destination: wdrDest.trim(),
      });
      toast.success("Retrait demandé — en attente admin");
      setWdrAmount(""); setWdrDest(""); setShowWithdraw(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitTransfer = async () => {
    if (!trfPhone.trim()) { toast.error("Numéro client requis"); return; }
    if (!trfAmount || Number(trfAmount) <= 0) { toast.error("Montant invalide"); return; }
    try {
      const res = await apiCall("post", "/affiliate/balance/transfer", {
        target_phone: trfPhone.trim(),
        amount: Number(trfAmount),
        note: trfNote,
      });
      toast.success(res.data.message || "Transfert effectué");
      setTrfPhone(""); setTrfAmount(""); setTrfNote(""); setShowTransfer(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const exchangeRate = useExchangeRate();

  return (
    <div className="card" data-testid={`balance-widget-${role}`} style={{ marginBottom: "0.8rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: "0.72rem", color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>
            <Wallet size={13} style={{ verticalAlign: "middle", color: "#2563EB" }} /> Mon solde
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#0F172A" }} data-testid="balance-amount">
            {formatUSD(balance, exchangeRate)}
          </div>
          {role !== "client" && (
            <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginTop: 1 }}>
              ≈ {formatHTG(balance)} · Taux {Number(exchangeRate).toFixed(2)}
            </div>
          )}
        </div>
        <button className="btn btn-sm" onClick={load} disabled={loading} style={{ background: "#E2E8F0" }} data-testid="balance-refresh">
          <RefreshCw size={12} /> {loading ? "..." : ""}
        </button>
      </div>

      <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
        <button className="btn btn-sm btn-primary" onClick={() => setShowDeposit(true)} data-testid="btn-balance-deposit">
          <ArrowDownCircle size={13} /> Recharger
        </button>
        <button className="btn btn-sm" onClick={() => setShowWithdraw(true)} style={{ background: "#FEE2E2", color: "#B91C1C" }} data-testid="btn-balance-withdraw">
          <ArrowUpCircle size={13} /> Retirer
        </button>
        {role === "affiliate" && (
          <button className="btn btn-sm" onClick={() => setShowTransfer(true)} style={{ background: "#DCFCE7", color: "#166534" }} data-testid="btn-balance-transfer">
            <Send size={13} /> Créditer un client
          </button>
        )}
      </div>

      {/* Pending deposits — visible status for the user that their recharge is awaiting admin validation */}
      {pendingDeposits.length > 0 && (
        <div data-testid="balance-pending-deposits" style={{ marginTop: "0.6rem", background: "#FEF3C7", border: "1px solid #F59E0B", borderRadius: 10, padding: "0.55rem 0.7rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <Clock size={14} style={{ color: "#B45309" }} />
            <div style={{ fontWeight: 800, color: "#92400E", fontSize: "0.78rem" }}>
              En attente de validation par l'administrateur
            </div>
          </div>
          <div style={{ fontSize: "0.7rem", color: "#92400E", marginBottom: 6 }}>
            L'administrateur traite votre demande sous 5-10 minutes. Le solde sera crédité automatiquement dès validation.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {pendingDeposits.slice(0, 5).map((d) => (
              <div key={d.id} data-testid={`balance-pending-deposit-${d.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF", borderRadius: 5, padding: "0.32rem 0.5rem", fontSize: "0.72rem" }}>
                <div>
                  <div style={{ fontWeight: 700, color: "#0F172A" }}>{formatUSD(d.amount, exchangeRate)}</div>
                  <div style={{ fontSize: "0.62rem", color: "#64748B" }}>{formatHTG(d.amount)} · {d.created_at ? new Date(d.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                </div>
                <span style={{ background: "#F59E0B", color: "#FFF", padding: "0.12rem 0.45rem", borderRadius: 999, fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.02em" }}>En attente</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {ledger.length > 0 && (
        <details style={{ marginTop: "0.6rem" }}>
          <summary style={{ fontSize: "0.72rem", color: "#64748B", cursor: "pointer" }}>Historique ({ledger.length})</summary>
          <div style={{ maxHeight: "180px", overflowY: "auto", marginTop: "0.3rem" }}>
            {ledger.slice(0, 30).map(e => (
              <div key={e.id} style={{ padding: "0.3rem 0.5rem", borderBottom: "1px solid #E2E8F0", fontSize: "0.72rem", display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748B" }}>{e.reason}</span>
                <span style={{ fontWeight: 700, color: e.kind === "debit" || e.kind === "transfer_out" || e.kind === "withdraw_locked" ? "#B91C1C" : "#166534" }}>
                  {(e.kind === "debit" || e.kind === "transfer_out" || e.kind === "withdraw_locked") ? "-" : "+"}{formatUSD(e.amount, exchangeRate)}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Deposit modal */}
      {showDeposit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={() => setShowDeposit(false)}>
          <div style={{ background: "white", borderRadius: "12px", padding: "1rem", maxWidth: "460px", width: "100%", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 0.5rem 0" }}>Recharger ma balance</h3>
            <p style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 0 }}>
              Envoyez l'argent à l'un des comptes admin ci-dessous, puis uploader la preuve.
            </p>
            <div style={{
              background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: "8px",
              padding: "0.5rem 0.6rem", fontSize: "0.72rem", color: "#166534",
              marginBottom: "0.55rem", lineHeight: 1.4,
            }}>
              ✅ <b>0 % de frais</b> sur la recharge et 0 % de frais sur l'achat de nos services.
              Vous recevez 100 % du montant envoyé.
            </div>

            {/* Admin QR codes */}
            {qrCodes.length > 0 ? (
              <div style={{ marginBottom: "0.6rem" }} data-testid="balance-dep-qr-list">
                <div style={{ fontSize: "0.7rem", color: "#475569", fontWeight: 700, marginBottom: "0.25rem", textTransform: "uppercase" }}>
                  Comptes de paiement admin
                </div>
                <div style={{ display: "grid", gap: "0.4rem" }}>
                  {qrCodes.map(q => (
                    <div key={q.id} style={{ padding: "0.5rem", background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: "8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.3rem", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "0.8rem", color: "#0C4A6E" }}>{q.label}</div>
                          <code style={{ fontSize: "0.72rem", color: "#0F172A" }}>{q.number}</code>
                          {q.note && <div style={{ fontSize: "0.65rem", color: "#64748B" }}>{q.note}</div>}
                        </div>
                        <button type="button" style={{ background: "#0EA5E9", color: "white", border: "none", borderRadius: "6px", padding: "0.2rem 0.5rem", fontSize: "0.7rem", cursor: "pointer" }}
                          onClick={() => { navigator.clipboard.writeText(q.number); toast.success("Copié!"); }}>
                          Copier
                        </button>
                      </div>
                      {q.qr_url && (
                        <img src={q.qr_url} alt="QR" style={{ display: "block", maxWidth: "140px", margin: "0.3rem auto 0", borderRadius: "6px" }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: "0.6rem", background: "#FEF3C7", borderRadius: "8px", marginBottom: "0.5rem" }} data-testid="balance-dep-fallback">
                <div style={{ fontSize: "0.72rem", color: "#92400E", fontWeight: 700, marginBottom: "0.35rem" }}>
                  Envoyez l'argent à l'un de ces comptes admin :
                </div>
                <div style={{ display: "grid", gap: "0.25rem", marginBottom: "0.4rem" }}>
                  {ADMIN_PAYMENT_NUMBERS.map(num => (
                    <div key={num} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: "white", borderRadius: "6px", padding: "0.3rem 0.5rem", border: "1px solid #FDE68A"
                    }}>
                      <code style={{ fontSize: "0.78rem", color: "#0F172A", fontWeight: 600 }}>{num}</code>
                      <button type="button"
                        onClick={() => { navigator.clipboard.writeText(num); toast.success("Copié!"); }}
                        style={{ background: "#0EA5E9", color: "white", border: "none", borderRadius: "5px", padding: "0.15rem 0.45rem", fontSize: "0.65rem", cursor: "pointer" }}
                      >
                        Copier
                      </button>
                    </div>
                  ))}
                </div>
                <a
                  href={ADMIN_WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="balance-dep-whatsapp"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
                    background: "#25D366", color: "white", padding: "0.5rem",
                    borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "0.78rem"
                  }}
                >
                  <Send size={14} /> Rejoindre le groupe WhatsApp officiel
                </a>
              </div>
            )}

            <input type="number" className="form-input" placeholder="Montant (Gdes)" value={depAmount} onChange={e => setDepAmount(e.target.value)} data-testid="balance-dep-amount" style={{ marginBottom: "0.4rem" }} />
            <label style={{ display: "block", padding: "0.5rem", border: "2px dashed #CBD5E1", borderRadius: "8px", textAlign: "center", cursor: "pointer", fontSize: "0.75rem", background: depProof ? "#F0FDF4" : "#F8FAFC" }}>
              <Upload size={13} /> {depUploading ? "Envoi..." : depProof ? "Preuve jointe ✓" : "Téléverser preuve"}
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleDepProof} data-testid="balance-dep-proof" />
            </label>
            <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem" }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={submitDeposit} data-testid="balance-dep-submit">Soumettre</button>
              <button className="btn" style={{ flex: 1, background: "#E2E8F0" }} onClick={() => setShowDeposit(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw modal */}
      {showWithdraw && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={() => setShowWithdraw(false)}>
          <div style={{ background: "white", borderRadius: "12px", padding: "1rem", maxWidth: "420px", width: "100%" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 0.5rem 0" }}>Retirer de ma balance</h3>
            <p style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 0 }}>
              Solde disponible: <b>{fmt(balance)} Gdes</b>
            </p>
            <div style={{
              background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "8px",
              padding: "0.5rem 0.6rem", fontSize: "0.72rem", color: "#7F1D1D",
              marginBottom: "0.55rem", lineHeight: 1.4,
            }}>
              ⚠ <b>Frais de retrait : 2 %</b> sur le montant demandé.
              {wdrAmount && Number(wdrAmount) > 0 && (() => {
                const a = Number(wdrAmount);
                const fee = Math.round(a * 0.02 * 100) / 100;
                const net = Math.round((a - fee) * 100) / 100;
                return (
                  <div style={{ marginTop: 4, color: "#991B1B" }}>
                    Vous recevrez : <b>{fmt(net)} Gdes</b> &nbsp;•&nbsp; Frais : {fmt(fee)} Gdes
                  </div>
                );
              })()}
            </div>
            <input type="number" className="form-input" placeholder="Montant" value={wdrAmount} onChange={e => setWdrAmount(e.target.value)} data-testid="balance-wdr-amount" style={{ marginBottom: "0.4rem" }} />
            <input type="text" className="form-input" placeholder="Numéro MonCash (+509...)" value={wdrDest} onChange={e => setWdrDest(e.target.value)} data-testid="balance-wdr-dest" />
            <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem" }}>
              <button className="btn btn-danger" style={{ flex: 1, background: "#EF4444", color: "white" }} onClick={submitWithdraw} data-testid="balance-wdr-submit">Demander</button>
              <button className="btn" style={{ flex: 1, background: "#E2E8F0" }} onClick={() => setShowWithdraw(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer modal (affiliate only) */}
      {showTransfer && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={() => setShowTransfer(false)}>
          <div style={{ background: "white", borderRadius: "12px", padding: "1rem", maxWidth: "420px", width: "100%" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 0.5rem 0" }}>Créditer un client</h3>
            <p style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 0 }}>
              Transférez de votre balance vers celle d'un de vos clients pour qu'il achète directement.
            </p>
            <input type="tel" className="form-input" placeholder="Numéro du client" value={trfPhone} onChange={e => setTrfPhone(e.target.value)} data-testid="balance-trf-phone" style={{ marginBottom: "0.4rem" }} />
            <input type="number" className="form-input" placeholder="Montant (Gdes)" value={trfAmount} onChange={e => setTrfAmount(e.target.value)} data-testid="balance-trf-amount" style={{ marginBottom: "0.4rem" }} />
            <input type="text" className="form-input" placeholder="Note (optionnel)" value={trfNote} onChange={e => setTrfNote(e.target.value)} />
            <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem" }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={submitTransfer} data-testid="balance-trf-submit">Transférer</button>
              <button className="btn" style={{ flex: 1, background: "#E2E8F0" }} onClick={() => setShowTransfer(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================================
// Admin Balance Manager
// ================================================================
export const AdminBalancePanel = ({ apiCall }) => {
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tab, setTab] = useState("deposits");
  const [proofView, setProofView] = useState(null);
  // Direct credit state
  const [creditOwnerType, setCreditOwnerType] = useState("affiliate");
  const [creditSearchTerm, setCreditSearchTerm] = useState("");
  const [creditUsers, setCreditUsers] = useState([]);

  // Memoize the filtered/sliced list so the .filter().slice() pipeline only
  // recomputes when the source data or the search term actually change —
  // not on every parent re-render.
  const filteredCreditUsers = useMemo(() => {
    const s = (creditSearchTerm || "").toLowerCase();
    return creditUsers
      .filter((u) => {
        if (!s) return true;
        return (u.name || "").toLowerCase().includes(s)
            || (u.code || "").toLowerCase().includes(s)
            || (u.phone || "").toLowerCase().includes(s);
      })
      .slice(0, 50);
  }, [creditUsers, creditSearchTerm]);
  const [creditSelected, setCreditSelected] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [creditSubmitting, setCreditSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, w] = await Promise.all([
        apiCall("get", "/admin/balance/deposits"),
        apiCall("get", "/admin/balance/withdrawals"),
      ]);
      setDeposits(d.data.deposits || []);
      setWithdrawals(w.data.withdrawals || []);
    } catch (e) { toast.error("Erreur"); }
  }, [apiCall]);

  // Load users (affiliates or clients) for credit autocomplete
  const loadCreditUsers = useCallback(async () => {
    try {
      const res = await apiCall("get", "/admin/balance/users");
      const items = (res.data.users || []).filter(u => u.owner_type === creditOwnerType);
      setCreditUsers(items);
    } catch { /* ignore */ }
  }, [apiCall, creditOwnerType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "credit") loadCreditUsers(); }, [tab, loadCreditUsers]);

  // Refresh on tab focus / visibility change AND poll every 20s while the
  // deposits/withdrawals tab is open + visible. This ensures new client
  // transactions appear quickly so the admin can validate without delay.
  useEffect(() => {
    const onFocus = () => load();
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    let timer = null;
    const inTxTab = tab === "deposits" || tab === "withdrawals";
    if (inTxTab) {
      timer = setInterval(() => {
        // Skip when the page is hidden to save bandwidth.
        if (document.visibilityState === "visible") load();
      }, 20000);
    }
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (timer) clearInterval(timer);
    };
  }, [load, tab]);

  const validateDep = async (id) => {
    if (!window.confirm("Valider ce dépôt et créditer le compte du client ?\n\nAprès validation, un message WhatsApp de confirmation s'ouvrira automatiquement.")) {
      return;
    }
    try {
      const res = await apiCall("post", `/admin/balance/deposit/${id}/validate`, { direct_admin: true });
      const data = res?.data || {};
      toast.success(`Dépôt validé · ${(data.credited || 0).toFixed(0)} Gdes crédités`);
      // Open WhatsApp confirmation to the client
      if (data.whatsapp_url) {
        try { window.open(data.whatsapp_url, "_blank", "noopener"); } catch (e) { /* ignore */ }
      } else {
        toast.error("Solde crédité, mais numéro WhatsApp manquant pour le client.");
      }
      load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const rejectDep = async (id) => {
    const reason = window.prompt("Raison du rejet (info incorrecte, sans avertissement) ?", "");
    if (reason === null) return;
    try { await apiCall("post", `/admin/balance/deposit/${id}/reject`, { reason }); toast.success("Rejeté"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  /* "Refuser pour fraude" path — issues a warning to the client, opens WhatsApp
     with the predefined warning message, and lists them in the surveillance
     panel. 3 warnings = the admin may then block the account. */
  const rejectDepFraud = async (id) => {
    const reason = window.prompt(
      "Motif de la fraude (visible dans le journal admin et l'avertissement) :",
      "Preuve falsifiée ou réutilisée",
    );
    if (reason === null) return;
    if (!window.confirm("Confirmer le refus pour FRAUDE ?\n\nUn avertissement sera enregistré et WhatsApp s'ouvrira pour notifier le client.")) return;
    try {
      const r = await apiCall("post", `/admin/balance/deposit/${id}/reject-v2`, { reason, kind: "fraud" });
      toast.warning(`Rejet fraude · avertissement ${r.data?.warning_count}/3`);
      const url = r.data?.whatsapp_url;
      if (url) { try { window.open(url, "_blank", "noopener"); } catch { /* noop */ } }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const completeWdr = async (id) => {
    const directAdmin = window.confirm(
      "Marquer ce retrait comme complété ?\n\nCliquez OK pour 'Traité directement par l'admin' (l'affilié ne reçoit aucune part).\nCliquez Annuler pour appliquer le partage 50/50 standard."
    );
    try {
      await apiCall("post", `/admin/balance/withdrawal/${id}/complete`, { direct_admin: directAdmin });
      toast.success(directAdmin ? "Retrait complété (admin direct, sans partage)" : "Retrait complété");
      load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const rejectWdr = async (id) => {
    const reason = window.prompt("Raison du rejet? (solde retourné)", "");
    if (reason === null) return;
    try { await apiCall("post", `/admin/balance/withdrawal/${id}/reject`, { reason }); toast.success("Retrait rejeté"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const pendingDeps = deposits.filter(d => d.status === "pending");
  const pendingWdrs = withdrawals.filter(w => w.status === "pending");

  return (
    <div className="card" data-testid="admin-balance-panel">
      <h3 className="card-title"><Wallet size={18} /> Balances utilisateurs</h3>
      <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.6rem", flexWrap: "wrap" }}>
        <button onClick={() => setTab("deposits")} className="btn btn-sm" style={{ background: tab === "deposits" ? "#2563EB" : "#E2E8F0", color: tab === "deposits" ? "white" : "#0F172A" }} data-testid="admin-balance-tab-deposits">Dépôts ({pendingDeps.length})</button>
        <button onClick={() => setTab("withdrawals")} className="btn btn-sm" style={{ background: tab === "withdrawals" ? "#2563EB" : "#E2E8F0", color: tab === "withdrawals" ? "white" : "#0F172A" }} data-testid="admin-balance-tab-withdrawals">Retraits ({pendingWdrs.length})</button>
        <button onClick={() => setTab("credit")} className="btn btn-sm" style={{ background: tab === "credit" ? "#16A34A" : "#E2E8F0", color: tab === "credit" ? "white" : "#0F172A" }} data-testid="admin-balance-tab-credit">Crédit direct</button>
      </div>

      {tab === "deposits" && (
        <div>
          {pendingDeps.length === 0 ? (
            <div style={{ padding: "1rem", textAlign: "center", color: "#64748B", fontSize: "0.8rem", background: "#F8FAFC", borderRadius: "8px" }}>Aucun dépôt en attente.</div>
          ) : pendingDeps.map(d => (
            <div key={d.id} className="card" style={{ marginBottom: "0.4rem" }} data-testid={`admin-balance-dep-${d.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "180px" }}>
                  <div style={{ fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {d.owner_name}
                    <span style={{ fontSize: "0.7rem", color: "#64748B" }}>({d.owner_type})</span>
                    {d.owner_phone && (() => {
                      const digits = String(d.owner_phone).replace(/\D/g, "");
                      if (!digits) return null;
                      const msg = "Votre recharge est en cours de traitement. Merci de patienter pendant la vérification du paiement. Cette opération prend généralement entre 2 et 5 minutes.";
                      return (
                        <a
                          href={`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`}
                          target="_blank" rel="noreferrer"
                          data-testid={`admin-balance-dep-wa-${d.id}`}
                          title={`Envoyer un message d'attente à ${d.owner_phone}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: "#16A34A", color: "#FFF",
                            padding: "0.15rem 0.45rem", borderRadius: 999,
                            fontSize: "0.7rem", fontWeight: 700, textDecoration: "none",
                          }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          {d.owner_phone}
                        </a>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: "0.82rem" }}><b>{fmt(d.amount)} Gdes</b></div>
                </div>
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  {(d.proof_url || d.has_proof) && (
                    <button className="btn btn-sm" style={{ background: "#E0E7FF", color: "#3730A3" }} onClick={async () => {
                      // Lazy-fetch the proof image only when admin asks to view it.
                      // The list endpoint no longer includes the heavy base64 payload.
                      if (d.proof_url) { setProofView(d.proof_url); return; }
                      try {
                        const r = await apiCall("get", `/admin/balance/deposits/${d.id}/proof`);
                        setProofView(r.data?.proof_url || "");
                      } catch { toast.error("Erreur preuve"); }
                    }}>Preuve</button>
                  )}
                  <button className="btn btn-sm btn-primary" onClick={() => validateDep(d.id)} data-testid={`admin-balance-dep-validate-${d.id}`}><CheckCircle size={13} /> Valider</button>
                  <button className="btn btn-sm" style={{ background: "#FEE2E2", color: "#B91C1C" }} onClick={() => rejectDep(d.id)} data-testid={`admin-balance-dep-reject-${d.id}`}><XCircle size={13} /> Refuser</button>
                  <button
                    className="btn btn-sm"
                    style={{ background: "#7F1D1D", color: "#FFF", fontWeight: 700 }}
                    onClick={() => rejectDepFraud(d.id)}
                    data-testid={`admin-balance-dep-reject-fraud-${d.id}`}
                    title="Refus pour fraude : avertissement + WhatsApp + ajout aux clients sous surveillance"
                  ><AlertTriangle size={13} /> Refuser (fraude)</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "withdrawals" && (
        <div>
          {pendingWdrs.length === 0 ? (
            <div style={{ padding: "1rem", textAlign: "center", color: "#64748B", fontSize: "0.8rem", background: "#F8FAFC", borderRadius: "8px" }}>Aucun retrait en attente.</div>
          ) : pendingWdrs.map(w => (
            <div key={w.id} className="card" style={{ marginBottom: "0.4rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "180px" }}>
                  <div style={{ fontWeight: 800 }}>{w.owner_name} <span style={{ fontSize: "0.7rem", color: "#64748B" }}>({w.owner_type})</span></div>
                  <div style={{ fontSize: "0.82rem" }}><b>{fmt(w.amount)} Gdes</b> → <code style={{ background: "#F1F5F9", padding: "0.1rem 0.3rem", borderRadius: "4px" }}>{w.destination}</code></div>
                </div>
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  <button className="btn btn-sm btn-primary" onClick={() => completeWdr(w.id)}><CheckCircle size={13} /> Complété</button>
                  <button className="btn btn-sm" style={{ background: "#FEE2E2", color: "#B91C1C" }} onClick={() => rejectWdr(w.id)}><XCircle size={13} /> Rejeter</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "credit" && (
        <div data-testid="admin-balance-credit-tab">
          <div style={{ background: "#F0FDF4", padding: "0.6rem", borderRadius: 8, marginBottom: "0.6rem", fontSize: "0.78rem", color: "#15803D", border: "1px solid #BBF7D0" }}>
            <strong>Crédit direct.</strong> Ajoutez un montant à un affilié ou client (sans frais). Cas typique : récompenser un affilié ou compenser une erreur.
          </div>
          <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
            <button className="btn btn-sm" style={{ background: creditOwnerType === "affiliate" ? "#2563EB" : "#E2E8F0", color: creditOwnerType === "affiliate" ? "white" : "#0F172A" }} onClick={() => { setCreditOwnerType("affiliate"); setCreditSelected(null); }} data-testid="credit-type-affiliate">Affilié</button>
            <button className="btn btn-sm" style={{ background: creditOwnerType === "client" ? "#2563EB" : "#E2E8F0", color: creditOwnerType === "client" ? "white" : "#0F172A" }} onClick={() => { setCreditOwnerType("client"); setCreditSelected(null); }} data-testid="credit-type-client">Client</button>
          </div>
          <input
            className="form-input"
            placeholder={creditOwnerType === "affiliate" ? "Rechercher un code/nom affilié…" : "Rechercher un nom/téléphone client…"}
            value={creditSearchTerm}
            onChange={(e) => setCreditSearchTerm(e.target.value)}
            style={{ marginBottom: "0.4rem", fontSize: "0.85rem" }}
            data-testid="credit-search"
          />
          <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8, marginBottom: "0.5rem" }}>
            {filteredCreditUsers
              .map((u) => (
                <div
                  key={u.owner_id}
                  onClick={() => setCreditSelected(u)}
                  style={{
                    padding: "0.4rem 0.6rem",
                    cursor: "pointer",
                    background: creditSelected?.owner_id === u.owner_id ? "#DBEAFE" : "#FFF",
                    borderBottom: "1px solid #F1F5F9",
                    fontSize: "0.82rem",
                  }}
                  data-testid={`credit-user-${u.owner_id}`}
                >
                  <div style={{ fontWeight: 700 }}>{u.name || u.code || u.phone || "—"}</div>
                  <div style={{ fontSize: "0.7rem", color: "#64748B" }}>{u.code || u.phone} · solde actuel : <b>{fmt(u.balance)} G</b></div>
                </div>
              ))}
            {creditUsers.length === 0 && (
              <div style={{ padding: "0.6rem", color: "#64748B", fontSize: "0.8rem", textAlign: "center" }}>Aucun utilisateur trouvé</div>
            )}
          </div>
          {creditSelected && (
            <div style={{ background: "#F8FAFC", padding: "0.6rem", borderRadius: 8, marginBottom: "0.5rem", fontSize: "0.82rem" }}>
              Crédit pour : <b>{creditSelected.name || creditSelected.code || creditSelected.phone}</b>
            </div>
          )}
          <input
            className="form-input"
            type="number"
            min="1"
            placeholder="Montant en Gdes"
            value={creditAmount}
            onChange={(e) => setCreditAmount(e.target.value)}
            style={{ marginBottom: "0.4rem" }}
            data-testid="credit-amount"
          />
          <input
            className="form-input"
            placeholder="Note (raison) — optionnel"
            value={creditNote}
            onChange={(e) => setCreditNote(e.target.value)}
            style={{ marginBottom: "0.5rem" }}
            data-testid="credit-note"
          />
          <button
            className="btn btn-primary"
            disabled={creditSubmitting || !creditSelected || !creditAmount}
            data-testid="credit-submit"
            onClick={async () => {
              const amount = parseFloat(creditAmount);
              if (!creditSelected) return;
              if (!amount || amount <= 0) { toast.error("Montant invalide"); return; }
              if (!window.confirm(`Créditer ${amount} Gdes à ${creditSelected.name || creditSelected.code || creditSelected.phone} ?`)) return;
              setCreditSubmitting(true);
              try {
                const res = await apiCall("post", "/admin/balance/credit", {
                  owner_type: creditOwnerType,
                  owner_id: creditSelected.owner_id,
                  amount,
                  note: creditNote || undefined,
                });
                toast.success("Crédit appliqué !");
                // Open WhatsApp to inform the user of the credit
                try {
                  // Resolve phone: backend response > local selection
                  const phone = (res?.data?.owner_phone || creditSelected.phone || "").toString();
                  const digits = phone.replace(/\D/g, "");
                  if (digits) {
                    const name = creditSelected.name || creditSelected.code || "";
                    const msg =
                      `Bonjour ${name}, votre solde SMARTECH SYSTEM PRO vient d'être crédité de ${amount} Gdes par l'administrateur.\n\n` +
                      (creditNote ? `📝 Note : ${creditNote}\n\n` : "") +
                      `Vous pouvez désormais utiliser ce solde pour acheter des services sur la plateforme.\n\n` +
                      `Merci de votre confiance.\n— SMARTECH SYSTEM PRO`;
                    const url = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
                    window.open(url, "_blank", "noopener");
                  } else {
                    toast.info("Crédit appliqué — numéro WhatsApp introuvable pour notifier l'utilisateur");
                  }
                } catch (e) { /* ignore */ }
                setCreditAmount("");
                setCreditNote("");
                setCreditSelected(null);
                loadCreditUsers();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Erreur");
              } finally {
                setCreditSubmitting(false);
              }
            }}
          >
            {creditSubmitting ? "Crédit en cours…" : "Créditer"}
          </button>
        </div>
      )}

      {proofView && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={() => setProofView(null)}>
          <img src={proofView} alt="preuve" style={{ maxWidth: "92%", maxHeight: "92%", borderRadius: "10px" }} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

// ================================================================
// Public Affiliate Selector (client picks / changes affiliate)
// ================================================================
export const AffiliateSelector = ({ apiCall, currentCode, onSelected, compact = false }) => {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/public/affiliates");
      setList(res.data.affiliates || []);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  }, [apiCall]);

  useEffect(() => { if (show) load(); }, [show, load]);

  const choose = async (code) => {
    try {
      await apiCall("post", "/client/choose-affiliate", { affiliate_code: code });
      toast.success(`Affilié sélectionné: ${code}`);
      setShow(false);
      if (onSelected) onSelected(code);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = q ? list.filter(a => (a.code + " " + a.name).toLowerCase().includes(q.toLowerCase())) : list;

  return (
    <>
      <button
        className={compact ? "btn btn-sm" : "btn btn-primary"}
        onClick={() => setShow(true)}
        data-testid="btn-open-affiliate-selector"
        style={compact ? { background: "#F1F5F9", color: "#0F172A", fontSize: "0.75rem" } : {}}
      >
        {currentCode ? `Affilié: ${currentCode}` : "Choisir un affilié"} <ArrowRight size={compact ? 11 : 14} />
      </button>

      {show && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={() => setShow(false)}>
          <div style={{ background: "white", borderRadius: "14px", padding: "1rem", maxWidth: "520px", width: "100%", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 0.4rem 0" }}>Choisissez votre affilié</h3>
            <p style={{ fontSize: "0.75rem", color: "#64748B", margin: "0 0 0.6rem 0" }}>
              {loading ? "Chargement..." : `${filtered.length} affilié(s) disponibles`}
            </p>
            <input className="form-input" placeholder="Rechercher..." value={q} onChange={e => setQ(e.target.value)} data-testid="affiliate-search" style={{ marginBottom: "0.5rem" }} />
            <div style={{ display: "grid", gap: "0.4rem" }}>
              {filtered.map(a => (
                <div key={a.id} style={{
                  padding: "0.5rem 0.7rem", border: "1px solid #E2E8F0", borderRadius: "10px",
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem",
                  background: a.code === currentCode ? "#EFF6FF" : "white"
                }} data-testid={`affiliate-item-${a.code}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <span style={{
                      display: "inline-block", width: "10px", height: "10px", borderRadius: "50%",
                      background: a.is_online ? "#10B981" : "#94A3B8",
                      boxShadow: a.is_online ? "0 0 6px rgba(16,185,129,0.5)" : "none",
                    }} title={a.is_online ? "En ligne" : "Hors ligne"} data-testid={`affiliate-online-${a.code}`} />
                    <div>
                      <div style={{ fontWeight: 800 }}>
                        {a.code}
                        {a.is_online && <span style={{ fontSize: "0.6rem", background: "#DCFCE7", color: "#166534", padding: "0.1rem 0.3rem", borderRadius: "4px", marginLeft: "0.3rem", fontWeight: 700 }}>EN LIGNE</span>}
                        {a.code === currentCode && <span style={{ fontSize: "0.65rem", background: "#DBEAFE", color: "#1E40AF", padding: "0.1rem 0.3rem", borderRadius: "4px", marginLeft: "0.3rem" }}>Actuel</span>}
                      </div>
                      <div style={{ fontSize: "0.7rem", color: "#64748B" }}>{a.name} · {a.level}</div>
                    </div>
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={() => choose(a.code)} disabled={a.code === currentCode} data-testid={`affiliate-choose-${a.code}`}>
                    Choisir
                  </button>
                </div>
              ))}
              {filtered.length === 0 && <div style={{ padding: "1rem", textAlign: "center", color: "#94A3B8", fontSize: "0.8rem" }}>Aucun résultat.</div>}
            </div>
            <button className="btn" style={{ width: "100%", marginTop: "0.5rem", background: "#E2E8F0" }} onClick={() => setShow(false)}>Fermer</button>
          </div>
        </div>
      )}
    </>
  );
};

export default BalanceWidget;

// ================================================================
// Affiliate presence toggle (I'm online / I'm offline)
// ================================================================
export const AffiliatePresenceToggle = ({ apiCall }) => {
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await apiCall("get", "/affiliate/presence");
      setIsOnline(!!r.data.is_online);
    } catch { /* ignore */ }
  }, [apiCall]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Heartbeat every 2 min while online
  useEffect(() => {
    if (!isOnline) return;
    const interval = setInterval(() => {
      apiCall("post", "/affiliate/presence", { online: true }).catch(() => {});
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isOnline, apiCall]);

  // Set offline on unmount / page close
  useEffect(() => {
    const goOffline = () => {
      navigator.sendBeacon?.(
        "/api/affiliate/presence",
        new Blob([JSON.stringify({ online: false })], { type: "application/json" })
      );
    };
    window.addEventListener("beforeunload", goOffline);
    return () => {
      window.removeEventListener("beforeunload", goOffline);
    };
  }, []);

  const toggle = async () => {
    setLoading(true);
    try {
      const next = !isOnline;
      const r = await apiCall("post", "/affiliate/presence", { online: next });
      setIsOnline(next);
      if (next && r.data.notified_clients > 0) {
        toast.success(`Vous êtes en ligne. ${r.data.notified_clients} client(s) notifié(s).`);
      } else if (next) {
        toast.success("Vous êtes en ligne.");
      } else {
        toast("Vous êtes hors ligne.");
      }
    } catch (e) {
      toast.error("Erreur");
    } finally { setLoading(false); }
  };

  return (
    <button
      onClick={toggle}
      disabled={loading}
      data-testid="affiliate-presence-toggle"
      className="btn btn-sm"
      style={{
        background: isOnline ? "#10B981" : "#94A3B8",
        color: "white",
        fontWeight: 700,
        border: "none",
        padding: "0.4rem 0.8rem",
        display: "flex",
        alignItems: "center",
        gap: "0.35rem",
      }}
    >
      <span style={{
        display: "inline-block", width: "10px", height: "10px", borderRadius: "50%",
        background: "white",
        boxShadow: isOnline ? "0 0 8px rgba(255,255,255,0.7)" : "none",
      }} />
      {isOnline ? "Je suis connecté" : "Je ne suis pas connecté"}
    </button>
  );
};

